/**
 * Helpers for attaching finding explainability/provenance (`additional_info`) to study-report
 * update payloads.
 *
 * When a clinician edits a finding on the web platform the value change is accompanied by a
 * provenance block marking the source as `"manual"`. The backend merges these blocks
 * non-destructively (see backend/docs/findings-explainability.md), so we only send the block(s)
 * for the finding(s) that actually changed, and — for now — only the `source`. The deciding
 * sweep / frame_range evidence is populated by the AI pipeline and will be wired up later.
 */

export type FindingSource = 'ai' | 'manual' | null;

/**
 * One sweep's proving frames. `sweep` + `frames` are raw engine indices; the remaining fields are
 * resolved by the backend at ingestion so the explainability modal can fetch & play the sweep clip.
 */
export interface FindingFrameRange {
  sweep?: number;
  frames?: number[];
  frame_indices?: number[];
  frame_ids?: string[];
  serie_instance_uid?: string;
  sop_instance_uid?: string;
  frame_count?: number;
  format_file?: 'DICOM' | 'VIDEO' | string;
  fps?: number | null;
  width?: number | null;
  height?: number | null;
  modality?: string | null;
  sop_class_uid?: string | null;
  [key: string]: unknown;
}

export type FindingAdditionalInfoBlock = {
  source?: FindingSource;
  value?: unknown;
  confidence?: string | number | null;
  frame_range?: FindingFrameRange[];
  evidence?: Record<string, unknown>;
} & Record<string, unknown>;

export type FindingAdditionalInfo = Record<string, FindingAdditionalInfoBlock>;


export const FINDING_FIELD_TO_EXPLAIN_KEY: Record<string, string> = {
  presentation: 'presentation',
  facing: 'presentation',
  is_twins: 'pregnancy_type',
  nb_fetuses: 'pregnancy_type',
  placenta_localization: 'placenta_localization',
  placenta_distance_to_cervix_class: 'placenta_distance_to_cervix',
  placenta_distance_to_cervix_cm: 'placenta_distance_to_cervix',
  cardiac_activity_present: 'cardiac_activity',
  heart_rate: 'cardiac_activity',
  sex_prediction: 'sex_prediction',
  pre_eclampsia: 'pre_eclampsia',
  gestational_diabetes: 'gestational_diabetes',
};

/**
 * Build the `additional_info` provenance blocks for the finding fields present in an update.
 *
 * @param updatedKeys keys of the update payload (only recognised finding keys are considered)
 * @param source provenance to stamp (defaults to `"manual"` for web edits)
 * @returns an `additional_info` map, or `undefined` when no finding field changed
 */
export function buildFindingProvenance(
  updatedKeys: string[],
  source: FindingSource = 'manual'
): FindingAdditionalInfo | undefined {
  const info: FindingAdditionalInfo = {};
  updatedKeys.forEach((key) => {
    const blockKey = FINDING_FIELD_TO_EXPLAIN_KEY[key];
    if (blockKey && !info[blockKey]) {
      info[blockKey] = { source };
    }
  });
  return Object.keys(info).length > 0 ? info : undefined;
}

/**
 * Merge freshly-built provenance blocks into an existing `additional_info` map (used for
 * optimistic cache updates). Non-destructive per block, mirroring the backend merge.
 */
export function mergeFindingAdditionalInfo(
  existing: FindingAdditionalInfo | undefined | null,
  incoming: FindingAdditionalInfo | undefined
): FindingAdditionalInfo | undefined {
  if (!incoming) {
    return existing ?? undefined;
  }
  const merged: FindingAdditionalInfo = { ...(existing ?? {}) };
  for (const [blockKey, block] of Object.entries(incoming)) {
    merged[blockKey] = { ...(merged[blockKey] ?? {}), ...block };
  }
  return merged;
}
